import { Button, FieldError, Input, Label, TextField } from "@heroui/react";
import { Icon } from "@iconify/react";
import { memo, useState } from "react";
import { StepHeading } from "@funnel-ui/primitives/StepHeading";

type EmailStepProps = {
  question?: string;
  description?: string;
  initialValue?: string;
  /** Show the privacy/trust badge below the input. */
  showTrustBadge?: boolean;
  trustText?: string;
  onChange?: (value: string) => void;
  onValidityChange?: (valid: boolean) => void;
  className?: string;
  testId?: string;
};

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Shared email validity check. Re-exported from the funnel-ui package
 * root so callers (e.g. funnel app steps that wrap `EmailStep` and need
 * to seed an initial validity flag) don't have to re-inline the regex
 * and risk drifting from `EmailStep`'s own validation. Trims first so
 * pasted whitespace doesn't flip the result.
 */
export const isValidEmail = (input: string): boolean => EMAIL_REGEX.test(input.trim());

const COMMON_TYPOS: Record<string, string> = {
  "gmail.co": "gmail.com",
  "gmial.com": "gmail.com",
  "gnail.com": "gmail.com",
  "yahoo.co": "yahoo.com",
  "hotnail.com": "hotmail.com",
};

const suggestCorrection = (email: string): string | undefined => {
  const at = email.indexOf("@");
  if (at < 0) return undefined;
  const domain = email.slice(at + 1).toLowerCase();
  const suggested = COMMON_TYPOS[domain];
  return suggested ? `${email.slice(0, at + 1)}${suggested}` : undefined;
};

const EmailStepImpl = ({
  question = "What's your email?",
  description = "We'll send your character details here.",
  initialValue = "",
  showTrustBadge = true,
  trustText = "We never share or sell your email.",
  onChange,
  onValidityChange,
  className,
  testId,
}: EmailStepProps) => {
  const [value, setValue] = useState(initialValue);

  const trimmed = value.trim();
  const isValid = EMAIL_REGEX.test(trimmed);
  const correction = suggestCorrection(trimmed);
  const errorMessage = trimmed.length > 0 && !isValid ? "Please enter a valid email." : undefined;

  const handleChange = (next: string): void => {
    setValue(next);
    onChange?.(next);
    onValidityChange?.(EMAIL_REGEX.test(next.trim()));
  };

  return (
    <section data-testid={testId} className={`flex flex-col gap-5 ${className ?? ""}`}>
      <StepHeading title={question} description={description} editablePath="question" />

      <TextField value={value} onChange={handleChange} isInvalid={!!errorMessage}>
        <Label className="sr-only">Email</Label>
        <Input
          type="email"
          inputMode="email"
          autoComplete="email"
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck={false}
          placeholder="you@example.com"
        />
        {errorMessage && <FieldError>{errorMessage}</FieldError>}
      </TextField>

      {correction && correction !== value && (
        <Button
          variant="ghost"
          size="sm"
          onPress={() => handleChange(correction)}
          className="h-auto p-0 text-left text-sm underline underline-offset-2"
          style={{ color: "hsl(var(--brand-primary))" }}
        >
          <span>
            Did you mean <strong>{correction}</strong>?
          </span>
        </Button>
      )}

      {showTrustBadge && (
        <div
          className="flex items-center gap-2 rounded-xl px-3 py-2 text-xs"
          style={{
            background: "hsl(var(--hero-success) / 0.08)",
            color: "hsl(var(--hero-success))",
          }}
        >
          <Icon icon="gravity-ui:lock" width={14} height={14} />
          <span>{trustText}</span>
        </div>
      )}
    </section>
  );
};

export const EmailStep = memo(EmailStepImpl);

export default EmailStep;
